import { create, type StoreApi, type UseBoundStore } from 'zustand';
import ServiceTabelasIncidencia from '@/services/ServiceTabelasIncidencia';
import { notify } from '@/stores/useToast';
import { getIncidenciaSpec } from '@/lib/incidenciaSpecs';

export interface LinhaIncidencia {
  [k: string]: unknown;
  code: string;
  name: string;
  notes: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

export type FormIncidencia = Record<string, unknown> & {
  code: string;
  name: string;
  notes: string | null;
};

interface State {
  lista: LinhaIncidencia[];
  total: number;
  page: number;
  size: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  busca: string;
  editando: string | null;
  carregando: boolean;
  salvando: boolean;
  exportando: boolean;
  historico: Record<string, unknown>[];
  setBusca: (v: string) => void;
  buscar: () => Promise<void>;
  setPage: (p: number) => Promise<void>;
  setSize: (s: number) => Promise<void>;
  setSort: (by: string, order: 'asc' | 'desc') => Promise<void>;
  setEditando: (code: string | null) => void;
  salvar: (form: FormIncidencia) => Promise<void>;
  carregarHistorico: (code: string) => Promise<void>;
  exportar: () => Promise<void>;
}

const _cache = new Map<string, UseBoundStore<StoreApi<State>>>();

function build(tabela: string): UseBoundStore<StoreApi<State>> {
  const exportAssincrono = getIncidenciaSpec(tabela)?.exportAssincrono ?? false;
  return create<State>((set, get) => ({
    lista: [],
    total: 0,
    page: 0,
    size: 50,
    sortBy: '',
    sortOrder: 'asc',
    busca: '',
    editando: null,
    carregando: false,
    salvando: false,
    exportando: false,
    historico: [],

    setBusca(v) {
      set({ busca: v });
    },

    async buscar() {
      const { busca, page, size, sortBy, sortOrder } = get();
      set({ carregando: true });
      try {
        // CON-5188: a key da coluna É o alias aceito pelo backend (identidade).
        const sort = sortBy ? `${sortBy},${sortOrder}` : undefined;
        const { data } = await ServiceTabelasIncidencia.search(
          tabela,
          { term: busca },
          { page, size, sort },
        );
        set({ lista: data.content ?? [], total: data.totalElements ?? 0 });
      } catch {
        set({ lista: [], total: 0 });
        notify.error('Erro ao carregar as incidências.');
      } finally {
        set({ carregando: false });
      }
    },

    async setPage(p) {
      set({ page: p });
      await get().buscar();
    },
    async setSize(s) {
      set({ size: s, page: 0 });
      await get().buscar();
    },
    async setSort(by, order) {
      set({ sortBy: by, sortOrder: order, page: 0 });
      await get().buscar();
    },
    setEditando(code) {
      set({ editando: code });
    },

    async salvar(form) {
      set({ salvando: true });
      try {
        const editando = get().editando;
        if (editando) {
          const { code, ...rest } = form;
          void code;
          await ServiceTabelasIncidencia.update(tabela, editando, rest);
        } else {
          await ServiceTabelasIncidencia.create(tabela, form);
        }
        notify.success('Incidência salva.');
        await get().buscar();
      } catch (e: any) {
        if (e?.response?.status === 409) notify.error('Código já existe.');
        else notify.error('Erro ao salvar a incidência.');
        throw e;
      } finally {
        set({ salvando: false });
      }
    },

    async carregarHistorico(code) {
      try {
        const { data } = await ServiceTabelasIncidencia.history(tabela, code);
        set({ historico: Array.isArray(data) ? data : [] });
      } catch {
        set({ historico: [] });
        notify.error('Erro ao carregar o histórico.');
      }
    },

    async exportar() {
      set({ exportando: true });
      try {
        const body = { term: get().busca };
        if (exportAssincrono) {
          await ServiceTabelasIncidencia.exportarPersistido(tabela, body);
          notify.success('Exportação em processamento. Acompanhe em Arquivos › Exportações.');
        } else {
          const { data } = await ServiceTabelasIncidencia.exportData(tabela, body);
          const url = URL.createObjectURL(data);
          const a = document.createElement('a');
          a.href = url;
          a.download = `incidencias-${tabela}.xlsx`;
          a.click();
          URL.revokeObjectURL(url);
        }
      } catch {
        notify.error('Não foi possível exportar.');
      } finally {
        set({ exportando: false });
      }
    },
  }));
}

export function getIncidenciaStore(tabela: string): UseBoundStore<StoreApi<State>> {
  let s = _cache.get(tabela);
  if (!s) {
    s = build(tabela);
    _cache.set(tabela, s);
  }
  return s;
}
